import type { HandoffPlatform } from '@usacsmideo/types';

/**
 * Verbatim consent copy for the "View in larger map" external handoff
 * (CU-86ajd3yt0, epic CU-86ajd302x). Kept in its own module (no component
 * export) so it is importable by tests + the dialog without a fast-refresh
 * violation, and so the PHI-facing wording lives in exactly one place.
 *
 * PROVISIONAL — pending compliance sign-off. Do NOT improvise this wording.
 * The load-bearing parts: (1) the TEMPORAL caveat — this is where the patient's
 * device was AT THE TIME OF THE SCAN, not their current location (echoing the
 * map caption; the third-party map can't carry it); (2) the single-pin /
 * no-accuracy-circle note. Native (Apple Maps) vs. desktop (web) variants differ
 * only in "maps app" vs "maps website".
 */

/** Verbatim native (Android/iOS) consent body. */
export const CONSENT_COPY_NATIVE =
  "This opens the scan location in your device's maps app, outside MIDEO. It shows " +
  "where the patient's device was at the time of the scan — not their current location " +
  '— as a single pin, without the accuracy circle. Continue?';

/** Verbatim desktop (web) consent body. */
export const CONSENT_COPY_DESKTOP =
  'This opens the scan location in a maps website, outside MIDEO. It shows where the ' +
  "patient's device was at the time of the scan — not their current location — as a " +
  'single pin, without the accuracy circle. Continue?';

/**
 * Directions variant. Adds, to the temporal caveat, that (on desktop) the viewer
 * will be asked to share THEIR OWN location so the map can route — disclosed up
 * front so the geolocation prompt isn't a surprise. Native maps apps supply the
 * origin themselves, so the "share your location" clause is desktop-only.
 */
export const CONSENT_COPY_DIRECTIONS_NATIVE =
  "This opens directions to the scan location in your device's maps app, outside " +
  "MIDEO. It routes to where the patient's device was at the time of the scan — not " +
  'their current location. Continue?';

export const CONSENT_COPY_DIRECTIONS_DESKTOP =
  'This opens directions to the scan location in a maps website, outside MIDEO. ' +
  "You'll be asked to share your own location so it can route from where you are. It " +
  "routes to where the patient's device was at the time of the scan — not their " +
  'current location. Continue?';

/** The action a consent gate is fronting — drives which copy variant shows. */
export type HandoffIntent = 'view' | 'directions';

/**
 * The consent-dialog body for a given platform + intent.
 * - view       → the pin/area copy (temporal caveat + single-pin note).
 * - directions → the routing copy (temporal caveat; desktop adds the
 *   share-your-location disclosure).
 */
export function consentCopyFor(platform: HandoffPlatform, intent: HandoffIntent = 'view'): string {
  if (intent === 'directions') {
    return platform === 'web' ? CONSENT_COPY_DIRECTIONS_DESKTOP : CONSENT_COPY_DIRECTIONS_NATIVE;
  }
  return platform === 'web' ? CONSENT_COPY_DESKTOP : CONSENT_COPY_NATIVE;
}
